import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Customer List - View customer details from GHL
 */
function CCCustomerList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch('/api/ghl/customers', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      } else if (response.status === 404) {
        // Endpoint not yet implemented
        setCustomers([]);
      } else {
        setError('Failed to load customers');
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `$${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Container className="py-4">
      <div className="mb-4">
        <Button
          variant="link"
          className="p-0 mb-2 text-decoration-none"
          style={{ color: colors.greyMid }}
          onClick={() => navigate('/command-center/market-intelligence')}
        >
          ← Back to Market Intelligence
        </Button>
        <h1>Customer List</h1>
        <p className="text-muted">
          View your customers, their lifetime value, and engagement history.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: colors.goldMid }} />
          <p className="mt-3 text-muted">Loading customers...</p>
        </div>
      ) : customers.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
            <h4>No Customer Data Yet</h4>
            <p className="text-muted mb-4">
              If you haven't launched yet, customers will appear here as you make sales.<br />
              If you have made sales, connect your payment processor to sync the data.
            </p>
            <Button
              variant="outline-dark"
              onClick={() => navigate('/command-center/settings')}
            >
              Connect Sales Platform
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead style={{ backgroundColor: colors.greyLightest }}>
                <tr>
                  <th className="border-0">Name</th>
                  <th className="border-0">Email</th>
                  <th className="border-0">Phone</th>
                  <th className="border-0 text-end">Lifetime Value</th>
                  <th className="border-0">Last Purchase</th>
                  <th className="border-0">Status</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <tr key={customer.id || index}>
                    <td className="align-middle">
                      <strong>{customer.name || customer.first_name + ' ' + customer.last_name || '-'}</strong>
                    </td>
                    <td className="align-middle">{customer.email || '-'}</td>
                    <td className="align-middle">{customer.phone || '-'}</td>
                    <td className="align-middle text-end">
                      <strong style={{ color: colors.goldDark }}>{formatCurrency(customer.ltv)}</strong>
                    </td>
                    <td className="align-middle">{formatDate(customer.last_purchase_date)}</td>
                    <td className="align-middle">
                      {customer.status === 'active' ? (
                        <Badge bg="success">Active</Badge>
                      ) : customer.status === 'churned' ? (
                        <Badge bg="danger">Churned</Badge>
                      ) : (
                        <Badge bg="secondary">{customer.status || 'Unknown'}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default CCCustomerList;
